import React, { useCallback, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMoon, FiSun} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toggleDarkMode } from '../../store/slices/uiSlice';
import { ProfileDropdown } from '../ProfileDropdown';
import LanguageSwitcher from '../LanguageSwitcher';
import { useThemeContext } from '../../components/ThemeProvider';
import { useTranslation } from 'react-i18next';

const IconButton = memo(({ icon: Icon, onClick, label, className = '' }) => {
  return (
    <motion.button
      aria-label={label}
      onClick={onClick}
      className={`
        relative p-2.5 rounded-xl 
        text-gray-600 dark:text-gray-300
        hover:text-gray-800 dark:hover:text-gray-100
        hover:bg-gray-100 dark:hover:bg-gray-700
        focus:outline-none focus:ring-2 focus:ring-blue-500
        transition-all duration-300 hover:scale-105
        ${className}
      `}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Icon className="w-5 h-5" />
    </motion.button>
  );
});

const Divider = memo(() => (
  <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-2" />
));

const Header = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const isDarkMode = useSelector((state) => state.ui.isDarkMode);
  
  // Dinamik tema renklerini al
  const { primaryColor } = useThemeContext();

  const handleToggleDarkMode = useCallback(() => {
    dispatch(toggleDarkMode());
  }, [dispatch]);

  // Professional header styles with glassmorphism effect and dynamic colors
  const headerStyles = useMemo(() => ({
    backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderBottomColor: isDarkMode ? '#374151' : '#e5e7eb',
    color: isDarkMode ? '#ffffff' : '#111827',
    boxShadow: isDarkMode 
      ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
      : '0 4px 20px rgba(0, 0, 0, 0.1)',
  }), [isDarkMode]);

  const headerClasses = `
    sticky top-0 z-30 transition-all duration-300
    border-b backdrop-blur-xl
  `;

  // Company branding section
  const BrandSection = memo(() => (
    <motion.div 
      className="flex items-center gap-4 ml-16 lg:ml-4"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
    </motion.div>
  ));

  // Action buttons section
  const ActionSection = memo(() => (
    <motion.div 
      className="flex items-center gap-1"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {/* Theme Toggle */}
      <IconButton
        icon={isDarkMode ? FiSun : FiMoon}
        onClick={handleToggleDarkMode}
        label={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
      />

      <Divider />

      {/* Language Switcher */}
      <div className="relative">
        <LanguageSwitcher primaryColor={primaryColor} />
      </div>

      <Divider />

      {/* User Profile */}
      <div className="relative ml-2">
        <ProfileDropdown />
      </div>
    </motion.div>
  ));

  return (
    <header className={headerClasses} style={headerStyles}>
      <div className="h-16 px-4 lg:px-6">
        <div className="flex items-center justify-between h-full">
          {/* Left Section */}
          <div className="flex items-center gap-6 flex-1">
            <BrandSection />
          </div>

          {/* Right Section */}
          <ActionSection />
        </div>
      </div>
    </header>
  );
};

export default memo(Header);